
import { Search, Menu, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CartIcon } from "./CartIcon";
import { NotificationDropdown } from "./NotificationDropdown";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useSearch } from "@/hooks/useSearch";
import { SearchDropdown } from "./SearchDropdown";

interface AppHeaderProps {
  title?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
}

export function AppHeader({ title = "Início", showMenuButton = false, onMenuClick }: AppHeaderProps) {
  const { displayName, initials, clientType, loading, avatarUrl, isAuthenticated } = useUserProfile();
  const { 
    query, 
    setQuery, 
    results, 
    isSearching, 
    showResults, 
    setShowResults,
    search, 
    clearSearch, 
    navigateToResult, 
    navigateToSearchPage 
  } = useSearch();

  // Only show loading skeleton on initial load, not on refetch
  if (loading && !isAuthenticated) {
    return (
      <header className="bg-card shadow-sm p-3 sm:p-4 flex items-center justify-between border-b h-16 flex-shrink-0 z-40">
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink">
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="md:hidden flex-shrink-0"
            >
              <Menu className="w-5 h-5" />
            </Button>
          )}
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-card-foreground truncate">
            {title}
          </h1>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-shrink-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted animate-pulse"></div>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-card shadow-sm p-3 sm:p-4 flex items-center justify-between border-b h-16 flex-shrink-0 z-40">
      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="md:hidden flex-shrink-0"
          >
            <Menu className="w-5 h-5" />
          </Button>
        )}
        <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-card-foreground truncate">
          {title}
        </h1>
      </div>

      <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-shrink-0">
        {/* Search - more responsive */}
        <div className="relative hidden lg:block">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input 
            type="text" 
            placeholder="Pesquisar..." 
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              search(e.target.value);
            }}
            onFocus={() => query && setShowResults(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                navigateToSearchPage();
              }
              if (e.key === 'Escape') {
                clearSearch();
              }
            }}
            className="pl-10 pr-10 w-40 xl:w-64 rounded-full border-input focus:ring-2 focus:ring-primary/20 focus:border-primary" 
          />
          {query && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearSearch}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
            >
              <X className="w-3 h-3" />
            </Button>
          )}
          
          {showResults && (
            <SearchDropdown
              results={results}
              isSearching={isSearching}
              query={query}
              onResultClick={navigateToResult}
              onViewAllClick={navigateToSearchPage}
            />
          )}
        </div>

        {/* Search icon for smaller screens */}
        <Button 
          variant="ghost" 
          size="icon" 
          className="lg:hidden flex-shrink-0"
          onClick={navigateToSearchPage}
        >
          <Search className="w-5 h-5" />
        </Button>

        {/* Cart */}
        <CartIcon />

        {/* Notifications */}
        <NotificationDropdown />

        {/* User Profile - more responsive */}
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          <Avatar className="w-8 h-8 sm:w-10 sm:h-10">
            <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
            <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden xl:block">
            <p className="text-sm font-medium text-card-foreground whitespace-nowrap">{displayName}</p>
            <p className="text-xs text-muted-foreground whitespace-nowrap">{clientType}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
